import { useEffect, useRef } from 'react';

export default function ParticleNetwork() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animFrame;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const NODE_COUNT = Math.min(80, Math.floor(window.innerWidth / 20));
    const CONNECTION_DIST = 150;
    const PULSE_INTERVAL = 3000;

    const nodes = Array.from({ length: NODE_COUNT }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.4,
      vy: (Math.random() - 0.5) * 0.4,
      size: Math.random() * 2 + 1,
      pulse: Math.random() > 0.85,
      pulsePhase: Math.random() * Math.PI * 2,
    }));

    const pulses = [];

    const addPulse = () => {
      const from = Math.floor(Math.random() * nodes.length);
      const neighbors = nodes
        .map((n, i) => ({ i, d: Math.hypot(n.x - nodes[from].x, n.y - nodes[from].y) }))
        .filter((n) => n.d < CONNECTION_DIST && n.i !== from)
        .sort((a, b) => a.d - b.d)
        .slice(0, 3);
      neighbors.forEach(({ i }) => {
        pulses.push({ from, to: i, t: 0, duration: 60 });
      });
    };

    const pulseInterval = setInterval(addPulse, PULSE_INTERVAL);

    let tick = 0;

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      tick++;

      // Update nodes
      nodes.forEach((n) => {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0 || n.x > canvas.width) n.vx *= -1;
        if (n.y < 0 || n.y > canvas.height) n.vy *= -1;
        n.pulsePhase += 0.05;
      });

      // Draw connections
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < CONNECTION_DIST) {
            const alpha = (1 - dist / CONNECTION_DIST) * 0.2;
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.strokeStyle = `rgba(30,144,255,${alpha})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }

      // Draw pulses
      for (let k = pulses.length - 1; k >= 0; k--) {
        const p = pulses[k];
        p.t++;
        if (p.t >= p.duration) { pulses.splice(k, 1); continue; }
        const progress = p.t / p.duration;
        const from = nodes[p.from];
        const to = nodes[p.to];
        const px = from.x + (to.x - from.x) * progress;
        const py = from.y + (to.y - from.y) * progress;
        ctx.beginPath();
        ctx.arc(px, py, 2.5, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(30,144,255,${1 - progress})`;
        ctx.fill();
        // Trail
        ctx.beginPath();
        const trail = Math.max(0, progress - 0.1);
        ctx.moveTo(from.x + (to.x - from.x) * trail, from.y + (to.y - from.y) * trail);
        ctx.lineTo(px, py);
        ctx.strokeStyle = `rgba(30,144,255,${(1 - progress) * 0.6})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      // Draw nodes
      nodes.forEach((n) => {
        const glow = n.pulse ? (Math.sin(n.pulsePhase) + 1) / 2 : 0;
        const size = n.size + glow * 2;
        ctx.beginPath();
        ctx.arc(n.x, n.y, size, 0, Math.PI * 2);
        ctx.fillStyle = n.pulse
          ? `rgba(30,144,255,${0.4 + glow * 0.6})`
          : 'rgba(30,144,255,0.5)';
        ctx.fill();
        if (n.pulse && glow > 0.5) {
          ctx.beginPath();
          ctx.arc(n.x, n.y, size * 2.5, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(30,144,255,${glow * 0.1})`;
          ctx.fill();
        }
      });

      animFrame = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(animFrame);
      clearInterval(pulseInterval);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      id="particle-canvas"
      aria-hidden="true"
      className="absolute inset-0 w-full h-full"
    />
  );
}
